import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Search, SearchX, ChevronDown, X, SlidersHorizontal, Clock, Reply, Gem, ShieldAlert, MessageSquareText, Tag, } from 'lucide-react';
import { Button, Calendar, DateRangePicker, DropdownMenu, Input, Label, Popover, Switch, TagInput, searchOptions, } from '@wit-support/ui';
import { ChannelBadge } from './channel-badge.js';
import { useAdminTimezone, startOfDayUtc, addDaysYmd } from './lib/time.js';
import { cn } from './lib/cn.js';
import { statusLabelRu } from './status-labels.js';
/**
 * Entering a search drops the status narrowing; leaving it puts the narrowing back.
 *
 * WHY. The inbox opens on `statuses: ['open']`, so every search ran inside the
 * open queue — and the one thing operators search for is a contact's history,
 * which is mostly closed. Both call recordings show the same manual dance:
 * «А можешь его почту скопировать? И в поиске вбить. …И нужно убрать фильтр
 * открыто», then «Всё, работает». The filter had to come off by hand every single
 * time; the machine can do it.
 *
 * Not silent: the removal is announced by a chip that puts the statuses back in one
 * click, and clearing the search restores them by itself — so the operator never
 * ends up in a state they didn't ask for and can't undo.
 *
 * Only the ENTER and LEAVE transitions act. Typing more characters inside an
 * already-active search must not re-suspend (there is nothing left to suspend) and
 * must not fight a status the operator deliberately picked while searching — such a
 * pick simply wins over the remembered set on the way out.
 *
 * Pure on purpose: all of the reasoning is here, testable without a DOM.
 */
export function applySearchStatusSuspension(value, nextQ) {
    const wasSearching = Boolean(value.q);
    const isSearching = Boolean(nextQ);
    if (isSearching && !wasSearching) {
        if (!value.statuses || value.statuses.length === 0)
            return { ...value, q: nextQ };
        return {
            ...value,
            q: nextQ,
            statuses: undefined,
            statusesSuspendedBySearch: value.statuses,
        };
    }
    if (!isSearching && wasSearching) {
        const remembered = value.statusesSuspendedBySearch;
        // A status chosen DURING the search is the operator's newer intent — keep it
        // and drop what we remembered rather than overwriting their choice.
        const restored = value.statuses?.length ? value.statuses : remembered;
        return {
            ...value,
            q: undefined,
            statuses: restored?.length ? restored : undefined,
            statusesSuspendedBySearch: undefined,
        };
    }
    return { ...value, q: nextQ };
}
const FilterBarContext = React.createContext(null);
function useFilterBarContext(component) {
    const ctx = React.useContext(FilterBarContext);
    if (!ctx) {
        throw new Error(`<${component}> must be rendered inside <FilterBar>`);
    }
    return ctx;
}
const FilterBarRoot = React.forwardRef(({ value, onChange, children, className }, ref) => {
    const ctx = React.useMemo(() => ({ value, onChange }), [value, onChange]);
    return (_jsx(FilterBarContext.Provider, { value: ctx, children: _jsx("div", { ref: ref, className: cn('flex flex-wrap items-center gap-2 border-b border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-3 py-2', className), children: children }) }));
});
FilterBarRoot.displayName = 'FilterBar';
const FilterBarSearch = React.forwardRef(({ placeholder = 'Search…', debounceMs = 300, className }, ref) => {
    const { value, onChange } = useFilterBarContext('FilterBar.Search');
    const [local, setLocal] = React.useState(value.q ?? '');
    const committed = React.useRef(value.q ?? '');
    React.useEffect(() => {
        const next = value.q ?? '';
        if (next !== committed.current) {
            setLocal(next);
            committed.current = next;
        }
    }, [value.q]);
    React.useEffect(() => {
        if (local === committed.current)
            return;
        const id = setTimeout(() => {
            committed.current = local;
            onChange(applySearchStatusSuspension(value, local.length > 0 ? local : undefined));
        }, debounceMs);
        return () => clearTimeout(id);
    }, [local, debounceMs, onChange, value]);
    const hasText = local.length > 0;
    return (_jsxs("div", { className: cn('relative min-w-0 flex-1', className), children: [_jsx(Search, { "aria-hidden": "true", className: "pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" }), _jsx(Input, { ref: ref, "data-filter-search": true, type: "search", role: "searchbox", placeholder: placeholder, value: local, onChange: (e) => setLocal(e.target.value), className: cn('pl-8', hasText && 'pr-8') }), hasText ? (_jsx("button", { type: "button", "aria-label": "Clear search", onClick: () => setLocal(''), className: "absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-[var(--radius-2)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-text-primary)]", children: _jsx(X, { className: "h-3.5 w-3.5", "aria-hidden": "true" }) })) : null] }));
});
FilterBarSearch.displayName = 'FilterBar.Search';
const FilterBarStatusFilter = React.forwardRef(({ statuses, className }, ref) => {
    const { value, onChange } = useFilterBarContext('FilterBar.StatusFilter');
    const selected = value.statuses ?? [];
    const label = selected.length === 0
        ? 'Все статусы'
        : selected.length === 1
            ? statusLabelRu(selected[0])
            : `Статусы: ${selected.length}`;
    function toggle(status) {
        const has = selected.includes(status);
        const next = has ? selected.filter((k) => k !== status) : [...selected, status];
        onChange({ ...value, statuses: next.length > 0 ? next : undefined });
    }
    return (_jsxs(DropdownMenu, { children: [_jsx(DropdownMenu.Trigger, { asChild: true, children: _jsxs(Button, { ref: ref, type: "button", variant: "secondary", size: "sm", className: cn(className), children: [label, _jsx(ChevronDown, { className: "ml-1 h-4 w-4 opacity-60", "aria-hidden": "true" })] }) }), _jsxs(DropdownMenu.Content, { children: [_jsx(DropdownMenu.Label, { children: "\u0421\u0442\u0430\u0442\u0443\u0441\u044B" }), statuses.map((status) => (_jsx(DropdownMenu.CheckboxItem, { checked: selected.includes(status), onCheckedChange: () => toggle(status), onSelect: (e) => e.preventDefault(), children: statusLabelRu(status) }, status)))] })] }));
});
FilterBarStatusFilter.displayName = 'FilterBar.StatusFilter';
function ymdFromUtcIso(iso, zone) {
    if (!iso)
        return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return '';
    const dtf = new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    return dtf.format(d);
}
function ymdFromExclusiveEnd(iso, zone) {
    if (!iso)
        return '';
    const exclusive = ymdFromUtcIso(iso, zone);
    if (!exclusive)
        return '';
    return addDaysYmd(exclusive, -1);
}
function ymdFromLocalDate(d) {
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
}
function localDateFromYmd(ymd) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!m)
        return undefined;
    const [, ys, mo, d] = m;
    return new Date(Number(ys), Number(mo) - 1, Number(d));
}
function formatRangeLabel(fromIso, toExclusiveIso, zone) {
    const fromYmd = ymdFromUtcIso(fromIso, zone);
    const toYmd = ymdFromExclusiveEnd(toExclusiveIso, zone);
    const fmt = (ymd) => {
        const d = localDateFromYmd(ymd);
        if (!d)
            return ymd;
        return new Intl.DateTimeFormat(undefined, { month: 'short', day: '2-digit' }).format(d);
    };
    if (fromYmd && toYmd)
        return `${fmt(fromYmd)} → ${fmt(toYmd)}`;
    if (fromYmd)
        return `${fmt(fromYmd)} → …`;
    if (toYmd)
        return `… → ${fmt(toYmd)}`;
    return '';
}
const FilterBarDateRange = React.forwardRef(({ className }, ref) => {
    const { value, onChange } = useFilterBarContext('FilterBar.DateRange');
    const tz = useAdminTimezone();
    const fromYmd = ymdFromUtcIso(value.fromUtc, tz);
    const toYmd = ymdFromExclusiveEnd(value.toUtcExclusive, tz);
    const pickerValue = {
        from: fromYmd ? localDateFromYmd(fromYmd) : undefined,
        to: toYmd ? localDateFromYmd(toYmd) : undefined,
    };
    function handleChange(next) {
        const nextFromUtc = next.from ? startOfDayUtc(ymdFromLocalDate(next.from), tz) : undefined;
        const nextToUtcExclusive = next.to
            ? startOfDayUtc(addDaysYmd(ymdFromLocalDate(next.to), 1), tz)
            : undefined;
        onChange({ ...value, fromUtc: nextFromUtc, toUtcExclusive: nextToUtcExclusive });
    }
    // Hidden accessible inputs preserve the existing {From,To} labels used by tests
    // while the visible control is the new DateRangePicker popover.
    function handleHiddenFrom(ymd) {
        handleChange({ ...pickerValue, from: ymd ? localDateFromYmd(ymd) : undefined });
    }
    function handleHiddenTo(ymd) {
        handleChange({ ...pickerValue, to: ymd ? localDateFromYmd(ymd) : undefined });
    }
    return (_jsxs("div", { ref: ref, className: cn('inline-flex', className), children: [_jsx(DateRangePicker, { value: pickerValue, onChange: handleChange, placeholder: "Date range", children: _jsx(DateRangePicker.Trigger, {}) }), _jsx("input", { type: "date", "aria-label": "From", value: fromYmd, onChange: (e) => handleHiddenFrom(e.target.value), className: "sr-only", tabIndex: -1 }), _jsx("input", { type: "date", "aria-label": "To", value: toYmd, onChange: (e) => handleHiddenTo(e.target.value), className: "sr-only", tabIndex: -1 })] }));
});
FilterBarDateRange.displayName = 'FilterBar.DateRange';
function countActiveFilters(f) {
    let n = 0;
    if (f.channelAccountIds && f.channelAccountIds.length > 0)
        n += 1;
    if (f.statuses && f.statuses.length > 0)
        n += 1;
    if (f.topicKeys && f.topicKeys.length > 0)
        n += 1;
    if (f.fromUtc || f.toUtcExclusive)
        n += 1;
    if (f.hasScheduledEmail)
        n += 1;
    if (f.awaitingOurReply)
        n += 1;
    if (f.isWhale)
        n += 1;
    if (f.hasChargeback)
        n += 1;
    if (f.hasComment)
        n += 1;
    if (f.tagIds && f.tagIds.length > 0)
        n += 1;
    return n;
}
/**
 * Есть ли хоть один активный фильтр, ВКЛЮЧАЯ строку поиска `q`. Единственный
 * источник ответа на «список пуст, потому что отфильтрован, или потому что
 * пуст на самом деле» — и для кнопки «Сбросить» самой панели, и для пустого
 * состояния инбокса. Новый фильтр добавляется сюда один раз, а не в каждую
 * копию по экранам.
 */
export function hasAnyFilter(f) {
    return Boolean((f.q && f.q.length > 0) ||
        (f.channelAccountIds && f.channelAccountIds.length > 0) ||
        (f.statuses && f.statuses.length > 0) ||
        (f.topicKeys && f.topicKeys.length > 0) ||
        f.fromUtc ||
        f.toUtcExclusive ||
        f.hasScheduledEmail ||
        f.awaitingOurReply ||
        f.isWhale ||
        f.hasChargeback ||
        f.hasComment ||
        (f.tagIds && f.tagIds.length > 0));
}
/**
 * The structured-filter keys wiped by every «Clear all» / «Reset» — ONE list
 * so a new filter cannot be added to the popover and forgotten by the reset
 * (which is exactly how `topicKeys` once got «stuck on»). `q` and
 * `statusesSuspendedBySearch` are deliberately NOT here: each caller decides
 * about the search separately (see the comments at the call sites).
 */
const CLEARED_STRUCTURED_FILTERS = {
    channelAccountIds: undefined,
    statuses: undefined,
    topicKeys: undefined,
    fromUtc: undefined,
    toUtcExclusive: undefined,
    hasScheduledEmail: undefined,
    awaitingOurReply: undefined,
    isWhale: undefined,
    hasChargeback: undefined,
    hasComment: undefined,
    tagIds: undefined,
};
/** A `Switch` + label row of the «Прочее» section. */
function FilterSwitchRow({ id, testId, checked, onCheckedChange, icon, label, className, }) {
    return (_jsxs("div", { className: cn('flex items-center gap-2', className), children: [_jsx(Switch, { id: id, checked: checked, onCheckedChange: onCheckedChange, "data-testid": testId }), _jsxs(Label, { htmlFor: id, className: "flex cursor-pointer select-none items-center gap-1.5 text-sm text-[var(--color-text-secondary)]", children: [icon, label] })] }));
}
/** Подпись выбранного тега, которого нет ни в выдаче, ни в известных (удалён из словаря). */
const UNKNOWN_TAG_LABEL = 'Тег недоступен';
/**
 * «Теги» section body: the kit `TagInput` — selected tags are chips INSIDE the
 * input row, typing searches. The search goes to the caller through a
 * debounced `onSearch` (the component knows nothing about the API — options
 * come back as a prop); without `onSearch` the given options are filtered
 * locally. Selected ids that are NOT among the current options (filtered out by
 * the search, or restored from storage) stay selected and keep their name from
 * `known` or from any option list this mount has seen.
 */
function TagPicker({ options, known, selected, onChange, onSearch, loading = false, debounceMs = 300, }) {
    const [local, setLocal] = React.useState('');
    /** Запрос набран, но ещё не ушёл (дебаунс) — ответа на него пока нет. */
    const [pending, setPending] = React.useState(false);
    const committed = React.useRef('');
    React.useEffect(() => {
        if (!onSearch || local === committed.current) {
            setPending(false);
            return;
        }
        setPending(true);
        const id = setTimeout(() => {
            committed.current = local;
            setPending(false);
            onSearch(local);
        }, debounceMs);
        return () => clearTimeout(id);
    }, [local, debounceMs, onSearch]);
    // Имена из всех выдач, что видел этот экземпляр: тег, выбранный до сужающего
    // поиска, обязан сохранить имя на чипе.
    const [seen, setSeen] = React.useState(() => new Map());
    React.useEffect(() => {
        if (options.length === 0)
            return;
        setSeen((prev) => {
            if (options.every((t) => prev.get(t.id) === t.name))
                return prev;
            const next = new Map(prev);
            for (const t of options)
                next.set(t.id, t.name);
            return next;
        });
    }, [options]);
    const nameOf = (id) => options.find((t) => t.id === id)?.name ??
        known?.find((t) => t.id === id)?.name ??
        seen.get(id) ??
        UNKNOWN_TAG_LABEL;
    const suggestions = (onSearch
        ? options
        : searchOptions(options.map((t) => ({ ...t, value: t.id, label: t.name })), local)).map((t) => ({ id: t.id, name: t.name }));
    return (_jsx("div", { "data-testid": "filter-tag-picker", children: _jsx(TagInput, { identity: "id", allowCreate: false, "aria-label": "\u0422\u0435\u0433\u0438", placeholder: "\u041D\u0430\u0439\u0442\u0438 \u0442\u0435\u0433\u2026", value: [...selected], getLabel: nameOf, suggestions: suggestions, query: local, onQueryChange: setLocal, onChange: onChange, onClear: () => onChange([]), indicator: true, maxTags: Number.MAX_SAFE_INTEGER, 
            // Пустая выдача, пока ответ на набранное ещё в пути, — «Загрузка…», а не
            // «Ничего не найдено»; непустая (прежняя) остаётся видна до ответа.
            loading: suggestions.length === 0 && (loading || pending), emptyLabel: local.trim() ? 'Ничего не найдено' : 'Тегов пока нет' }) }));
}
/** Shared toggle-pill used by the channels/statuses/topics sections of FilterBar.Filters. */
function ToggleChip({ active, onClick, className, children }) {
    return (_jsx("button", { type: "button", onClick: onClick, "aria-pressed": active, className: cn('inline-flex h-7 items-center rounded-[var(--radius-2)] border px-2 text-xs transition-colors', active
            ? 'border-[var(--color-brand-bg)] bg-[var(--color-brand-bg)] text-[var(--color-brand-fg)]'
            : 'border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-overlay)]', className), children: children }));
}
/** Unified "Filters" popover: channels + statuses + topics + tags + toggles + date range in a single panel. */
const FilterBarFilters = React.forwardRef(({ channels = [], statuses, topicOptions = [], tagOptions, onTagSearch, knownTags, tagOptionsLoading = false, className, }, ref) => {
    const { value, onChange } = useFilterBarContext('FilterBar.Filters');
    const tz = useAdminTimezone();
    const selectedChannelIds = value.channelAccountIds ?? [];
    const selectedStatuses = value.statuses ?? [];
    const selectedTopicKeys = value.topicKeys ?? [];
    const selectedTagIds = value.tagIds ?? [];
    const showTags = tagOptions !== undefined || onTagSearch !== undefined;
    const count = countActiveFilters(value);
    function toggleChannelId(id) {
        const next = selectedChannelIds.includes(id)
            ? selectedChannelIds.filter((k) => k !== id)
            : [...selectedChannelIds, id];
        onChange({ ...value, channelAccountIds: next.length > 0 ? next : undefined });
    }
    function toggleStatus(status) {
        const next = selectedStatuses.includes(status)
            ? selectedStatuses.filter((k) => k !== status)
            : [...selectedStatuses, status];
        onChange({ ...value, statuses: next.length > 0 ? next : undefined });
    }
    function toggleTopicKey(key) {
        const next = selectedTopicKeys.includes(key)
            ? selectedTopicKeys.filter((k) => k !== key)
            : [...selectedTopicKeys, key];
        onChange({ ...value, topicKeys: next.length > 0 ? next : undefined });
    }
    function toggleScheduledEmail(checked) {
        onChange({ ...value, hasScheduledEmail: checked ? true : undefined });
    }
    function toggleAwaitingOurReply(checked) {
        onChange({ ...value, awaitingOurReply: checked ? true : undefined });
    }
    function toggleIsWhale(checked) {
        onChange({ ...value, isWhale: checked ? true : undefined });
    }
    function toggleHasChargeback(checked) {
        onChange({ ...value, hasChargeback: checked ? true : undefined });
    }
    function toggleHasComment(checked) {
        onChange({ ...value, hasComment: checked ? true : undefined });
    }
    function setTagIds(next) {
        onChange({ ...value, tagIds: next.length > 0 ? next : undefined });
    }
    const fromYmd = ymdFromUtcIso(value.fromUtc, tz);
    const toYmd = ymdFromExclusiveEnd(value.toUtcExclusive, tz);
    const selectedRange = fromYmd || toYmd
        ? {
            from: fromYmd ? localDateFromYmd(fromYmd) : undefined,
            to: toYmd ? localDateFromYmd(toYmd) : undefined,
        }
        : undefined;
    function handleDateSelect(range) {
        const from = range?.from;
        const to = range?.to;
        const nextFromUtc = from ? startOfDayUtc(ymdFromLocalDate(from), tz) : undefined;
        const nextToUtcExclusive = to
            ? startOfDayUtc(addDaysYmd(ymdFromLocalDate(to), 1), tz)
            : undefined;
        onChange({ ...value, fromUtc: nextFromUtc, toUtcExclusive: nextToUtcExclusive });
    }
    function handleHiddenFrom(ymd) {
        const from = ymd ? localDateFromYmd(ymd) : undefined;
        handleDateSelect({ from, to: selectedRange?.to });
    }
    function handleHiddenTo(ymd) {
        const to = ymd ? localDateFromYmd(ymd) : undefined;
        handleDateSelect({ from: selectedRange?.from, to });
    }
    function clearAll() {
        // Intentionally spreads `...value` so that `q` (the search query) is
        // preserved. Search has its own clear affordance (the ✕ inside the search
        // input), so clearing structured filters should not reset it.  This
        // differs from FilterBar.Reset which explicitly sets `q: undefined` to
        // wipe everything.
        onChange({ ...value, ...CLEARED_STRUCTURED_FILTERS });
    }
    return (_jsxs(Popover, { children: [_jsx(Popover.Trigger, { asChild: true, children: _jsxs(Button, { ref: ref, type: "button", variant: "secondary", size: "sm", "aria-label": count > 0 ? `Фильтры (активно: ${count})` : 'Фильтры', title: "\u0424\u0438\u043B\u044C\u0442\u0440\u044B", className: cn('relative shrink-0', className), children: [_jsx(SlidersHorizontal, { className: "h-4 w-4 opacity-80", "aria-hidden": "true" }), count > 0 ? (_jsx("span", { className: "absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[var(--color-brand-bg)] ring-2 ring-[var(--color-surface-canvas)]", "aria-hidden": "true" })) : null] }) }), _jsx(Popover.Content, { className: "w-[640px] max-w-[calc(100vw-1rem)] p-0", align: "start", sideOffset: 6, children: _jsxs("div", { className: "flex flex-col", children: [channels.length > 0 ? (_jsxs("div", { className: "p-3", children: [_jsx(Label, { className: "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]", children: "Channels" }), _jsx("div", { className: "flex flex-wrap gap-1.5", children: channels.map((ch) => {
                                        const active = selectedChannelIds.includes(ch.id);
                                        return (_jsxs(ToggleChip, { active: active, onClick: () => toggleChannelId(ch.id), className: "gap-1.5", children: [_jsx(ChannelBadge, { kind: ch.kind, size: "sm", iconOnly: true, className: "!bg-transparent !p-0" }), _jsx("span", { className: "truncate max-w-[180px]", children: ch.name })] }, ch.id));
                                    }) })] })) : null, _jsxs("div", { className: cn('p-3', channels.length > 0 && 'border-t border-[var(--color-border-subtle)]'), children: [_jsx(Label, { className: "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]", children: "\u0421\u0442\u0430\u0442\u0443\u0441\u044B" }), _jsx("div", { className: "flex flex-wrap gap-1.5", children: statuses.map((status) => {
                                        const active = selectedStatuses.includes(status);
                                        return (_jsx(ToggleChip, { active: active, onClick: () => toggleStatus(status), className: "gap-1", children: statusLabelRu(status) }, status));
                                    }) })] }), topicOptions.length > 0 ? (_jsxs("div", { className: "border-t border-[var(--color-border-subtle)] p-3", children: [_jsx(Label, { className: "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]", children: "Topics" }), _jsx("div", { className: "flex flex-wrap gap-1.5", children: topicOptions.map((topic) => {
                                        const active = selectedTopicKeys.includes(topic.key);
                                        return (_jsx(ToggleChip, { active: active, onClick: () => toggleTopicKey(topic.key), className: "gap-1", children: _jsx("span", { className: "truncate max-w-[180px]", children: topic.name }) }, topic.key));
                                    }) })] })) : null, showTags ? (_jsxs("div", { className: "border-t border-[var(--color-border-subtle)] p-3", children: [_jsx(Label, { className: "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]", children: "\u0422\u0435\u0433\u0438" }), _jsx(TagPicker, { options: tagOptions ?? [], known: knownTags, loading: tagOptionsLoading, selected: selectedTagIds, onChange: setTagIds, onSearch: onTagSearch })] })) : null, _jsxs("div", { className: "border-t border-[var(--color-border-subtle)] p-3", children: [_jsx(Label, { className: "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]", children: "Date range" }), _jsx(Calendar, { mode: "range", numberOfMonths: 2, selected: selectedRange, onSelect: handleDateSelect, className: "p-0" }), _jsx("input", { type: "date", "aria-label": "From", value: fromYmd, onChange: (e) => handleHiddenFrom(e.target.value), className: "sr-only", tabIndex: -1 }), _jsx("input", { type: "date", "aria-label": "To", value: toYmd, onChange: (e) => handleHiddenTo(e.target.value), className: "sr-only", tabIndex: -1 })] }), _jsxs("div", { className: "border-t border-[var(--color-border-subtle)] p-3", children: [_jsx(Label, { className: "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]", children: "\u041F\u0440\u043E\u0447\u0435\u0435" }), _jsx(FilterSwitchRow, { id: "filter-scheduled", testId: "filter-has-scheduled", checked: value.hasScheduledEmail === true, onCheckedChange: toggleScheduledEmail, icon: _jsx(Clock, { className: "h-3.5 w-3.5", "aria-hidden": "true" }), label: "\u0421 \u043E\u0442\u043B\u043E\u0436\u0435\u043D\u043D\u044B\u043C\u0438 \u043F\u0438\u0441\u044C\u043C\u0430\u043C\u0438" }), _jsx(FilterSwitchRow, { id: "filter-awaiting-reply", testId: "filter-awaiting-reply", checked: value.awaitingOurReply === true, onCheckedChange: toggleAwaitingOurReply, icon: _jsx(Reply, { className: "h-3.5 w-3.5", "aria-hidden": "true" }), label: "\u0416\u0434\u0451\u0442 \u043D\u0430\u0448\u0435\u0433\u043E \u043E\u0442\u0432\u0435\u0442\u0430", className: "mt-2" }), _jsx(FilterSwitchRow, { id: "filter-is-whale", testId: "filter-is-whale", checked: value.isWhale === true, onCheckedChange: toggleIsWhale, icon: _jsx(Gem, { className: "h-3.5 w-3.5", "aria-hidden": "true" }), label: "\u041A\u0438\u0442\u044B", className: "mt-2" }), _jsx(FilterSwitchRow, { id: "filter-has-chargeback", testId: "filter-has-chargeback", checked: value.hasChargeback === true, onCheckedChange: toggleHasChargeback, icon: _jsx(ShieldAlert, { className: "h-3.5 w-3.5", "aria-hidden": "true" }), label: "\u0427\u0430\u0440\u0434\u0436\u0431\u0435\u043A\u0438", className: "mt-2" }), _jsx(FilterSwitchRow, { id: "filter-has-comment", testId: "filter-has-comment", checked: value.hasComment === true, onCheckedChange: toggleHasComment, icon: _jsx(MessageSquareText, { className: "h-3.5 w-3.5", "aria-hidden": "true" }), label: "\u0421 \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0435\u043C", className: "mt-2" })] }), _jsxs("div", { className: "flex items-center justify-between gap-2 border-t border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-2", children: [_jsxs(Button, { type: "button", variant: "ghost", size: "sm", onClick: clearAll, disabled: count === 0, children: [_jsx(X, { className: "mr-1 h-3.5 w-3.5", "aria-hidden": "true" }), "Reset"] }), _jsx(Popover.Close, { asChild: true, children: _jsx(Button, { type: "button", variant: "primary", size: "sm", children: "Apply" }) })] })] }) })] }));
});
FilterBarFilters.displayName = 'FilterBar.Filters';
function Chip({ children, onRemove, ariaLabel }) {
    return (_jsxs("span", { className: "inline-flex h-7 items-center gap-1.5 rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] pl-2 pr-1 text-xs text-[var(--color-text-primary)]", children: [_jsx("span", { className: "flex items-center gap-1.5", children: children }), _jsx("button", { type: "button", "aria-label": ariaLabel, onClick: onRemove, className: "flex h-5 w-5 items-center justify-center rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-text-primary)]", children: _jsx(X, { className: "h-3 w-3", "aria-hidden": "true" }) })] }));
}
/**
 * Renders a flex-wrap row of active filter chips (one per channel id, one
 * per status, one for the date range). Each chip has its own X to drop just
 * that filter; a trailing "Clear all" appears when any chip is present.
 * Render this below the toolbar so users always see what's applied without
 * reopening the Filters popover.
 */
const FilterBarActiveFilters = React.forwardRef(({ channels = [], topicOptions = [], tagOptions = [], className }, ref) => {
    const { value, onChange } = useFilterBarContext('FilterBar.ActiveFilters');
    const tz = useAdminTimezone();
    const channelIndex = React.useMemo(() => {
        const m = new Map();
        for (const c of channels)
            m.set(c.id, c);
        return m;
    }, [channels]);
    const topicIndex = React.useMemo(() => {
        const m = new Map();
        for (const t of topicOptions)
            m.set(t.key, t);
        return m;
    }, [topicOptions]);
    const tagIndex = React.useMemo(() => {
        const m = new Map();
        for (const t of tagOptions)
            m.set(t.id, t);
        return m;
    }, [tagOptions]);
    const selectedChannelIds = value.channelAccountIds ?? [];
    const selectedStatuses = value.statuses ?? [];
    const selectedTopicKeys = value.topicKeys ?? [];
    const selectedTagIds = value.tagIds ?? [];
    const hasDate = Boolean(value.fromUtc || value.toUtcExclusive);
    const hasScheduled = value.hasScheduledEmail === true;
    const isAwaiting = value.awaitingOurReply === true;
    const isWhaleOn = value.isWhale === true;
    const hasChargebackOn = value.hasChargeback === true;
    const hasCommentOn = value.hasComment === true;
    // Only while the search is actually on: a leftover remembered set with no
    // search must never advertise itself (and cannot happen — leaving the search
    // clears it).
    const suspendedStatuses = value.q ? (value.statusesSuspendedBySearch ?? []) : [];
    // Render only when this row has at least one active filter chip of its own.
    const any = selectedChannelIds.length > 0 ||
        selectedStatuses.length > 0 ||
        selectedTopicKeys.length > 0 ||
        selectedTagIds.length > 0 ||
        hasDate ||
        hasScheduled ||
        isAwaiting ||
        isWhaleOn ||
        hasChargebackOn ||
        hasCommentOn ||
        suspendedStatuses.length > 0;
    if (!any)
        return null;
    function removeChannel(id) {
        const next = selectedChannelIds.filter((x) => x !== id);
        onChange({ ...value, channelAccountIds: next.length > 0 ? next : undefined });
    }
    function removeStatus(s) {
        const next = selectedStatuses.filter((x) => x !== s);
        onChange({ ...value, statuses: next.length > 0 ? next : undefined });
    }
    function removeTopic(key) {
        const next = selectedTopicKeys.filter((x) => x !== key);
        onChange({ ...value, topicKeys: next.length > 0 ? next : undefined });
    }
    function removeDate() {
        onChange({ ...value, fromUtc: undefined, toUtcExclusive: undefined });
    }
    function removeScheduled() {
        onChange({ ...value, hasScheduledEmail: undefined });
    }
    function removeAwaiting() {
        onChange({ ...value, awaitingOurReply: undefined });
    }
    function removeTag(id) {
        const next = selectedTagIds.filter((x) => x !== id);
        onChange({ ...value, tagIds: next.length > 0 ? next : undefined });
    }
    function removeWhale() {
        onChange({ ...value, isWhale: undefined });
    }
    function removeChargeback() {
        onChange({ ...value, hasChargeback: undefined });
    }
    function removeComment() {
        onChange({ ...value, hasComment: undefined });
    }
    // The chip's ✕ RESTORES rather than removes: what it represents is the absence
    // of a status filter, so undoing it puts the statuses back (search stays on).
    function restoreSuspendedStatuses() {
        onChange({ ...value, statuses: suspendedStatuses, statusesSuspendedBySearch: undefined });
    }
    function clearAll() {
        // Intentionally spreads `...value` so that `q` (the search query) is
        // preserved. Search has its own clear affordance (the ✕ inside the search
        // input), so clearing structured filters should not reset it.  This
        // differs from FilterBar.Reset which explicitly sets `q: undefined` to
        // wipe everything.
        onChange({
            ...value,
            ...CLEARED_STRUCTURED_FILTERS,
            statusesSuspendedBySearch: undefined,
        });
    }
    return (_jsxs("div", { ref: ref, className: cn('flex flex-wrap items-center gap-1.5 border-b border-[var(--color-border-subtle)] bg-[var(--color-surface-canvas)] px-4 py-2', className), "data-testid": "filter-chips", children: [selectedChannelIds.map((id) => {
                const ch = channelIndex.get(id);
                const label = ch?.name ?? id.slice(0, 8);
                return (_jsxs(Chip, { onRemove: () => removeChannel(id), ariaLabel: `Remove channel filter ${label}`, children: [ch ? (_jsx(ChannelBadge, { kind: ch.kind, size: "sm", iconOnly: true, className: "!bg-transparent !p-0" })) : null, _jsx("span", { className: "truncate max-w-[160px]", children: label })] }, `ch-${id}`));
            }), selectedStatuses.map((s) => (_jsx(Chip, { onRemove: () => removeStatus(s), ariaLabel: `Убрать фильтр статуса ${statusLabelRu(s)}`, children: _jsx("span", { children: statusLabelRu(s) }) }, `st-${s}`))), selectedTopicKeys.map((key) => {
                const label = topicIndex.get(key)?.name ?? key;
                return (_jsx(Chip, { onRemove: () => removeTopic(key), ariaLabel: `Remove topic filter ${label}`, children: _jsx("span", { className: "truncate max-w-[160px]", children: label }) }, `tp-${key}`));
            }), selectedTagIds.map((id) => {
                // A tag the page does not know (deleted since the filter was saved,
                // or its options not loaded yet) gets a human label, never a uuid
                // fragment — the chip stays removable either way.
                const label = tagIndex.get(id)?.name ?? 'Тег недоступен';
                return (_jsxs(Chip, { onRemove: () => removeTag(id), ariaLabel: `Убрать фильтр по тегу ${label}`, children: [_jsx(Tag, { className: "h-3 w-3 text-[var(--color-text-muted)]", "aria-hidden": "true" }), _jsx("span", { className: "truncate max-w-[160px]", children: label })] }, `tg-${id}`));
            }), hasDate ? (_jsx(Chip, { onRemove: removeDate, ariaLabel: "Remove date filter", children: _jsx("span", { children: formatRangeLabel(value.fromUtc, value.toUtcExclusive, tz) || 'Date' }) })) : null, hasScheduled ? (_jsx("span", { "data-testid": "scheduled-active-chip", className: "contents", children: _jsxs(Chip, { onRemove: removeScheduled, ariaLabel: "\u0423\u0431\u0440\u0430\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440 \u043E\u0442\u043B\u043E\u0436\u0435\u043D\u043D\u044B\u0445 \u043F\u0438\u0441\u0435\u043C", children: [_jsx(Clock, { className: "h-3 w-3 text-[var(--color-text-muted)]", "aria-hidden": "true" }), _jsx("span", { children: "\u0421 \u043E\u0442\u043B\u043E\u0436\u0435\u043D\u043D\u044B\u043C\u0438 \u043F\u0438\u0441\u044C\u043C\u0430\u043C\u0438" })] }) })) : null, isAwaiting ? (_jsx("span", { "data-testid": "awaiting-active-chip", className: "contents", children: _jsxs(Chip, { onRemove: removeAwaiting, ariaLabel: "\u0423\u0431\u0440\u0430\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440 \u00AB\u0416\u0434\u0451\u0442 \u043D\u0430\u0448\u0435\u0433\u043E \u043E\u0442\u0432\u0435\u0442\u0430\u00BB", children: [_jsx(Reply, { className: "h-3 w-3 text-[var(--color-text-muted)]", "aria-hidden": "true" }), _jsx("span", { children: "\u0416\u0434\u0451\u0442 \u043D\u0430\u0448\u0435\u0433\u043E \u043E\u0442\u0432\u0435\u0442\u0430" })] }) })) : null, isWhaleOn ? (_jsx("span", { "data-testid": "whale-active-chip", className: "contents", children: _jsxs(Chip, { onRemove: removeWhale, ariaLabel: "\u0423\u0431\u0440\u0430\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440 \u00AB\u041A\u0438\u0442\u044B\u00BB", children: [_jsx(Gem, { className: "h-3 w-3 text-[var(--color-text-muted)]", "aria-hidden": "true" }), _jsx("span", { children: "\u041A\u0438\u0442\u044B" })] }) })) : null, hasChargebackOn ? (_jsx("span", { "data-testid": "chargeback-active-chip", className: "contents", children: _jsxs(Chip, { onRemove: removeChargeback, ariaLabel: "\u0423\u0431\u0440\u0430\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440 \u00AB\u0427\u0430\u0440\u0434\u0436\u0431\u0435\u043A\u0438\u00BB", children: [_jsx(ShieldAlert, { className: "h-3 w-3 text-[var(--color-text-muted)]", "aria-hidden": "true" }), _jsx("span", { children: "\u0427\u0430\u0440\u0434\u0436\u0431\u0435\u043A\u0438" })] }) })) : null, hasCommentOn ? (_jsx("span", { "data-testid": "comment-active-chip", className: "contents", children: _jsxs(Chip, { onRemove: removeComment, ariaLabel: "\u0423\u0431\u0440\u0430\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440 \u00AB\u0421 \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0435\u043C\u00BB", children: [_jsx(MessageSquareText, { className: "h-3 w-3 text-[var(--color-text-muted)]", "aria-hidden": "true" }), _jsx("span", { children: "\u0421 \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0435\u043C" })] }) })) : null, suspendedStatuses.length > 0 ? (_jsx("span", { "data-testid": "suspended-statuses-chip", className: "contents", children: _jsxs(Chip, { onRemove: restoreSuspendedStatuses, ariaLabel: `Вернуть фильтр статуса: ${suspendedStatuses.map(statusLabelRu).join(', ')}`, children: [_jsx(SearchX, { className: "h-3 w-3 text-[var(--color-text-muted)]", "aria-hidden": "true" }), _jsx("span", { children: "\u041F\u043E\u0438\u0441\u043A \u043F\u043E \u0432\u0441\u0435\u043C \u0441\u0442\u0430\u0442\u0443\u0441\u0430\u043C" })] }) })) : null, _jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: clearAll, className: "h-7 px-2", children: "Clear all" })] }));
});
FilterBarActiveFilters.displayName = 'FilterBar.ActiveFilters';
const FilterBarReset = React.forwardRef(({ className, label = 'Reset' }, ref) => {
    const { value, onChange } = useFilterBarContext('FilterBar.Reset');
    const active = hasAnyFilter(value);
    return (_jsxs(Button, { ref: ref, type: "button", variant: "ghost", size: "sm", className: cn(className), disabled: !active, onClick: () => onChange({
            q: undefined,
            ...CLEARED_STRUCTURED_FILTERS,
            statusesSuspendedBySearch: undefined,
        }), children: [_jsx(X, { className: "mr-1 h-3.5 w-3.5", "aria-hidden": "true" }), label] }));
});
FilterBarReset.displayName = 'FilterBar.Reset';
export const FilterBar = Object.assign(FilterBarRoot, {
    Search: FilterBarSearch,
    StatusFilter: FilterBarStatusFilter,
    DateRange: FilterBarDateRange,
    Filters: FilterBarFilters,
    ActiveFilters: FilterBarActiveFilters,
    Reset: FilterBarReset,
});
//# sourceMappingURL=filter-bar.js.map